/**
 * Dynamically loads and initializes `data-component` modules on the page.
 */
class ComponentHelper {
    /**
     * Normalize a `data-component` value (`type/Name`) for dynamic import.
     *
     * @param {string} rawValue Attribute value like `03_molecules/Accordion`
     * @returns {string|null} Path segment `type/Name`, or null if invalid
     */
    static prepareComponentPath(rawValue) {
        if (rawValue.indexOf('/') === -1) {
            return null;
        }
        const typeName = rawValue.split('/')[0].toLowerCase();

        let componentName = rawValue.split('/')[1];
        componentName = componentName.substr(0, 1).toUpperCase() + componentName.substr(1);

        return `${typeName}/${componentName}`;
    }

    /**
     * Initialize all `[data-component]` nodes under a parent.
     *
     * @param {ParentNode} [parentNode=document] Root to query within
     */
    initializeDataComponents(parentNode = document) {
        for (const node of parentNode.querySelectorAll('[data-component]')) {
            this.initializeDataComponent(node);
        }
    }

    /**
     * Dynamically import and instantiate one component for a node.
     * Skips nodes already marked (`data-component-init`) so early boot and
     * `window.load` cannot double-instantiate the same element.
     *
     * @param {HTMLElement} node Element with `data-component`
     */
    initializeDataComponent(node) {
        if (node.dataset.componentInit === '1') {
            return;
        }

        const componentPath = ComponentHelper.prepareComponentPath(node.getAttribute('data-component'));

        if (typeof componentPath !== 'string') {
            return;
        }

        // Mark before async import so overlapping boot paths cannot race.
        node.dataset.componentInit = '1';

        import(/* webpackChunkName: "[request]", webpackInclude: /\.js$/ */ `../../../components/${componentPath}`).then((Component) => {
            const comp = new Component.default(node); // eslint-disable-line new-cap
            comp.init();
        }).catch((e) => {
            // Silently ignore "Cannot find module" errors for CSS-only components
            const isModuleNotFoundError = e.message && e.message.includes('Cannot find module');

            if (isModuleNotFoundError) {
                // Silently ignore CSS-only components (no JavaScript needed)
                return;
            }

            // Log other errors in all modes
            // eslint-disable-next-line no-console
            console.error('ComponentHelper: Error loading component', componentPath, e);
        });
    }

    /**
     * Initialize all `[data-component]` elements on the document.
     */
    initializePage() {
        const immediateComponents = document.querySelectorAll('[data-component]');

        for (const component of immediateComponents) {
            this.initializeDataComponent(component);
        }
    }
}

export default ComponentHelper;
